import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'user-baseline-collection' | 'partner-baseline-collection' | 'relationship-snapshot' | 'relationship-context' | 'full-relationship-diagnostic';
export function RelationshipDecode() {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(true);
  const [hasUserBaseline] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasUserBaseline ? 'partner-baseline-collection' : 'user-baseline-collection');
  const [showExplanation, setShowExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  // User baseline data
  const [userBaselineData, setUserBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Partner baseline data
  const [partnerBaselineData, setPartnerBaselineData] = useState({
    partnerName: '',
    dateOfBirth: '1992-06-20',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1992',
    month: '06',
    day: '20',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Relationship context data
  const [relationshipContext, setRelationshipContext] = useState({
    relationshipType: '',
    durationBand: '',
    tensionZones: [] as string[],
    contextNote: ''
  });
  // Relationship snapshot (baseline only)
  const relationshipSnapshot = {
    attractionMechanics: 'This bond formed through unconscious complementarity. One system stabilizes through internal structure and clarity; the other through external connection and emotional expression. The attraction is mutual — each system holds what the other lacks. The pull is not conscious choice but structural fit.',
    patternFit: 'These systems fit through stabilizing loops when both are regulated, and amplifying loops when pressure rises. One system over-functions cognitively; the other over-functions emotionally. This creates balance under low pressure but asymmetry under high pressure. Neither system is wrong — the fit is structural.',
    frictionZones: 'Tension is inevitable where one system needs space to process and the other needs connection to regulate. Where one system seeks clarity before action and the other seeks action to create clarity. Where one system withdraws under pressure and the other pursues. This is pattern collision, not personal failure.',
    currentBondPhase: 'This relationship is in the power negotiation phase. The initial bonding has stabilized. Now, each system is testing boundaries, control, and autonomy. Friction is higher than in the bonding phase but lower than in separation readiness. This phase determines whether the bond matures into integration or fragments.'
  };
  // Full diagnostic output
  const fullDiagnosticOutput = {
    structuralInteraction: 'One system stabilizes through analysis and structure. The other through connection and emotional expression. Under low pressure, this creates complementarity. Under high pressure, it creates distance and pursuit.',
    currentExpression: 'After 3-7 years together, this interaction is expressing through communication friction and distance/closeness tension. The stabilizing loops that worked early in the bond are now creating friction. The system that withdraws is withdrawing more; the system that pursues is pursuing harder. The bond is testing whether it can recalibrate or whether the pattern will lock.',
    mutualTriggers: "One person triggers the other's fear of emotional overwhelm. The other triggers the first person's fear of abandonment. Neither person is doing this intentionally. The triggers are structural — each system's regulation pattern activates the other's core sensitivity. This is why the same conflict repeats even when intent is good.",
    growthBreakpointBoundary: 'This bond will mature if both systems can hold their own regulation without requiring the other to change. It will break if one system continues to require the other to regulate differently, or if the pursuit-withdrawal pattern locks into a stable loop. Growth is not about effort or love — it is about system capacity to hold tension without collapsing into blame.',
    whatChanged: {
      baseline: 'The baseline showed attraction mechanics (unconscious complementarity), pattern fit (stabilizing and amplifying loops), friction zones (space vs connection, clarity vs action), and current phase (power negotiation).',
      context: 'Adding relationship type (romantic), duration (3-7 years), and tension zones (communication, distance/closeness) revealed how the structural interaction is expressing in lived experience right now — and where the system is under the most strain.',
      whyConflictNotIncompatibility: 'Conflict is not incompatibility. Conflict is two systems trying to regulate in ways that collide. The baseline shows why the collision is inevitable. Context shows where it is happening right now. Neither person is the problem — the interaction pattern is.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateUserDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setUserBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setUserBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const updatePartnerDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setPartnerBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setPartnerBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateUserTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setUserBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const updatePartnerTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setPartnerBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleUserBaselineSubmit = () => {
    if (!userBaselineData.dateOfBirth || !userBaselineData.placeOfBirth || !userBaselineData.country) return;
    setIsGenerating(true);
    setGeneratingMessage('Saving your baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('partner-baseline-collection');
    }, 1500);
  };
  const handlePartnerBaselineSubmit = () => {
    if (!partnerBaselineData.partnerName || !partnerBaselineData.dateOfBirth || !partnerBaselineData.placeOfBirth || !partnerBaselineData.country) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating relationship snapshot...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('relationship-snapshot');
    }, 2000);
  };
  const handleRelationshipContextSubmit = () => {
    setIsGenerating(true);
    setGeneratingMessage('Integrating context...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('full-relationship-diagnostic');
    }, 2000);
  };
  const handleToggleTensionZone = (zone: string) => {
    setRelationshipContext(prev => ({
      ...prev,
      tensionZones: prev.tensionZones.includes(zone) ? prev.tensionZones.filter(z => z !== zone) : [...prev.tensionZones, zone]
    }));
  };
  if (isGenerating) {
    return <>
        <Helmet data-id="element-2707">
          <title data-id="element-2708">Generating Relationship Report | Lazy Monk Labs</title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-2709">
          <div className="text-center" data-id="element-2710">
            <div className="mb-6" data-id="element-2711">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-2712"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-2713">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-2714">
        <title data-id="element-2715">Relationship Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2716">
        <header data-id="element-2717">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2718">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2719">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2720">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2721" />
                <span className="text-lg font-medium tracking-tight" data-id="element-2722">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2723">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-2724">
          <div className="max-w-2xl mx-auto" data-id="element-2725">
            {/* Phases will be added in next edit due to length */}

            {/* PHASE 1A: User Baseline Collection - Abbreviated baseline UI */}
            {currentPhase === 'user-baseline-collection' && <div data-id="element-2726">
                <div className="mb-12" data-id="element-2727">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2728">
                    Relationship Decode
                  </h1>
                  <p className="text-lg text-[#F5F5F5]/60 italic mb-4" data-id="element-2729">
                    A diagnostic of how two systems interact
                  </p>
                  <p className="text-base text-[#F5F5F5]/70 leading-relaxed" data-id="element-2730">
                    This report maps attraction, friction, and growth dynamics —
                    without blame or prediction.
                  </p>
                </div>
                <div className="mb-8" data-id="element-2731">
                  <h2 className="text-xl font-medium mb-4" data-id="element-2732">Your baseline</h2>
                </div>
                <div className="space-y-8 mb-12" data-id="element-2733">
                  <div data-id="element-2734">
                    <label className="block text-sm font-medium mb-3" data-id="element-2735">
                      Date of birth
                    </label>
                    {userBaselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-2736">
                        <div className="text-2xl font-medium" data-id="element-2737">
                          {months.find(m => m.value === userBaselineData.month)?.label}{' '}
                          {userBaselineData.day}, {userBaselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-2738">
                      <div data-id="element-2739">
                        <label htmlFor="userMonth" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2740">
                          Month
                        </label>
                        <select id="userMonth" value={userBaselineData.month} onChange={e => updateUserDateOfBirth(userBaselineData.year, e.target.value, userBaselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2741">
                          <option value="" data-id="element-2742">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-2743">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2744">
                        <label htmlFor="userDay" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2745">
                          Day
                        </label>
                        <select id="userDay" value={userBaselineData.day} onChange={e => updateUserDateOfBirth(userBaselineData.year, userBaselineData.month, e.target.value)} disabled={!userBaselineData.month || !userBaselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-2746">
                          <option value="" data-id="element-2747">Day</option>
                          {Array.from({
                        length: getDaysInMonth(userBaselineData.year, userBaselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-2748">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2749">
                        <label htmlFor="userYear" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2750">
                          Year
                        </label>
                        <select id="userYear" value={userBaselineData.year} onChange={e => updateUserDateOfBirth(e.target.value, userBaselineData.month, userBaselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2751">
                          <option value="" data-id="element-2752">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-2753">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-2754">
                    <label htmlFor="userPlace" className="block text-sm font-medium mb-2" data-id="element-2755">
                      Place of birth
                    </label>
                    <input type="text" id="userPlace" value={userBaselineData.placeOfBirth} onChange={e => setUserBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2756" />
                  </div>
                  <div data-id="element-2757">
                    <label htmlFor="userCountry" className="block text-sm font-medium mb-2" data-id="element-2758">
                      Country
                    </label>
                    <input type="text" id="userCountry" value={userBaselineData.country} onChange={e => setUserBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2759" />
                  </div>
                </div>
                <div data-id="element-2760">
                  <label className="block text-sm font-medium mb-3" data-id="element-2761">
                    Time of birth{' '}
                    <span className="text-[#F5F5F5]/40" data-id="element-2762">(optional)</span>
                  </label>
                  <div className="space-y-3 mb-4" data-id="element-2763">
                    {[{
                  value: 'exact',
                  label: 'I know the exact time'
                }, {
                  value: 'approximate',
                  label: 'I know approximately'
                }, {
                  value: 'not-sure',
                  label: "Not sure / Don't know"
                }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${userBaselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2764">
                        <input type="radio" name="userTimeKnown" value={option.value} checked={userBaselineData.timeKnown === option.value} onChange={e => setUserBaselineData(prev => ({
                    ...prev,
                    timeKnown: e.target.value,
                    timeOfBirth: '',
                    hour: '12',
                    minute: '00',
                    period: 'AM'
                  }))} className="sr-only" data-id="element-2765" />
                        <span className="text-sm" data-id="element-2766">{option.label}</span>
                      </label>)}
                  </div>
                  {(userBaselineData.timeKnown === 'exact' || userBaselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-2767">
                      <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-2768">
                        <div className="text-center mb-8" data-id="element-2769">
                          <div className="text-4xl font-medium tracking-tight" data-id="element-2770">
                            {userBaselineData.hour}:{userBaselineData.minute}{' '}
                            {userBaselineData.period}
                          </div>
                          <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-2771">
                            {userBaselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                          </p>
                        </div>
                        <div className="mb-6" data-id="element-2772">
                          <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2773">
                            Hour
                          </label>
                          <div className="grid grid-cols-6 gap-2" data-id="element-2774">
                            {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updateUserTimeOfBirth(h, userBaselineData.minute, userBaselineData.period)} className={`py-3 text-sm font-medium transition-colors ${userBaselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2775">
                                {h}
                              </button>)}
                          </div>
                        </div>
                        <div className="mb-6" data-id="element-2776">
                          <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2777">
                            Minute
                          </label>
                          <div className="grid grid-cols-6 gap-2" data-id="element-2778">
                            {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updateUserTimeOfBirth(userBaselineData.hour, m, userBaselineData.period)} className={`py-3 text-sm font-medium transition-colors ${userBaselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2779">
                                {m}
                              </button>)}
                          </div>
                          <div className="mt-2" data-id="element-2780">
                            <input type="number" min="0" max="59" value={userBaselineData.minute} onChange={e => {
                        const val = e.target.value.padStart(2, '0');
                        if (parseInt(val) >= 0 && parseInt(val) <= 59) updateUserTimeOfBirth(userBaselineData.hour, val, userBaselineData.period);
                      }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2781" />
                          </div>
                        </div>
                        <div data-id="element-2782">
                          <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2783">
                            Period
                          </label>
                          <div className="grid grid-cols-2 gap-2" data-id="element-2784">
                            {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updateUserTimeOfBirth(userBaselineData.hour, userBaselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${userBaselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2785">
                                {p}
                              </button>)}
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>
                <button onClick={handleUserBaselineSubmit} disabled={!userBaselineData.dateOfBirth || !userBaselineData.placeOfBirth || !userBaselineData.country} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2786">
                  Continue to Partner Baseline
                </button>
              </div>}

            {/* PHASE 1B: Partner Baseline Collection */}
            {currentPhase === 'partner-baseline-collection' && <div data-id="element-2787">
                <div className="mb-12" data-id="element-2788">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2789">
                    Partner Baseline
                  </h1>
                </div>
                <div className="space-y-8 mb-12" data-id="element-2790">
                  <div data-id="element-2791">
                    <label htmlFor="partnerName" className="block text-sm font-medium mb-2" data-id="element-2792">
                      Partner's name
                    </label>
                    <input type="text" id="partnerName" value={partnerBaselineData.partnerName} onChange={e => setPartnerBaselineData(prev => ({
                  ...prev,
                  partnerName: e.target.value
                }))} placeholder="First name" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2793" />
                  </div>
                  <div data-id="element-2794">
                    <label className="block text-sm font-medium mb-3" data-id="element-2795">
                      Date of birth
                    </label>
                    {partnerBaselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-2796">
                        <div className="text-2xl font-medium" data-id="element-2797">
                          {months.find(m => m.value === partnerBaselineData.month)?.label}{' '}
                          {partnerBaselineData.day}, {partnerBaselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-2798">
                      <div data-id="element-2799">
                        <label htmlFor="partnerMonth" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2800">
                          Month
                        </label>
                        <select id="partnerMonth" value={partnerBaselineData.month} onChange={e => updatePartnerDateOfBirth(partnerBaselineData.year, e.target.value, partnerBaselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2801">
                          <option value="" data-id="element-2802">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-2803">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2804">
                        <label htmlFor="partnerDay" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2805">
                          Day
                        </label>
                        <select id="partnerDay" value={partnerBaselineData.day} onChange={e => updatePartnerDateOfBirth(partnerBaselineData.year, partnerBaselineData.month, e.target.value)} disabled={!partnerBaselineData.month || !partnerBaselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-2806">
                          <option value="" data-id="element-2807">Day</option>
                          {Array.from({
                        length: getDaysInMonth(partnerBaselineData.year, partnerBaselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-2808">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2809">
                        <label htmlFor="partnerYear" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2810">
                          Year
                        </label>
                        <select id="partnerYear" value={partnerBaselineData.year} onChange={e => updatePartnerDateOfBirth(e.target.value, partnerBaselineData.month, partnerBaselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2811">
                          <option value="" data-id="element-2812">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-2813">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-2814">
                    <label htmlFor="partnerPlace" className="block text-sm font-medium mb-2" data-id="element-2815">
                      Place of birth
                    </label>
                    <input type="text" id="partnerPlace" value={partnerBaselineData.placeOfBirth} onChange={e => setPartnerBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2816" />
                  </div>
                  <div data-id="element-2817">
                    <label htmlFor="partnerCountry" className="block text-sm font-medium mb-2" data-id="element-2818">
                      Country
                    </label>
                    <input type="text" id="partnerCountry" value={partnerBaselineData.country} onChange={e => setPartnerBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2819" />
                  </div>
                  <div data-id="element-2820">
                    <label className="block text-sm font-medium mb-3" data-id="element-2821">
                      Time of birth{' '}
                      <span className="text-[#F5F5F5]/40" data-id="element-2822">(optional)</span>
                    </label>
                    <div className="space-y-3 mb-4" data-id="element-2823">
                      {[{
                    value: 'exact',
                    label: 'I know the exact time'
                  }, {
                    value: 'approximate',
                    label: 'I know approximately'
                  }, {
                    value: 'not-sure',
                    label: "Not sure / Don't know"
                  }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${partnerBaselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2824">
                          <input type="radio" name="partnerTimeKnown" value={option.value} checked={partnerBaselineData.timeKnown === option.value} onChange={e => setPartnerBaselineData(prev => ({
                      ...prev,
                      timeKnown: e.target.value,
                      timeOfBirth: '',
                      hour: '12',
                      minute: '00',
                      period: 'AM'
                    }))} className="sr-only" data-id="element-2825" />
                          <span className="text-sm" data-id="element-2826">{option.label}</span>
                        </label>)}
                    </div>
                    {(partnerBaselineData.timeKnown === 'exact' || partnerBaselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-2827">
                        <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-2828">
                          <div className="text-center mb-8" data-id="element-2829">
                            <div className="text-4xl font-medium tracking-tight" data-id="element-2830">
                              {partnerBaselineData.hour}:
                              {partnerBaselineData.minute}{' '}
                              {partnerBaselineData.period}
                            </div>
                            <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-2831">
                              {partnerBaselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                            </p>
                          </div>
                          <div className="mb-6" data-id="element-2832">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2833">
                              Hour
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-2834">
                              {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updatePartnerTimeOfBirth(h, partnerBaselineData.minute, partnerBaselineData.period)} className={`py-3 text-sm font-medium transition-colors ${partnerBaselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2835">
                                  {h}
                                </button>)}
                            </div>
                          </div>
                          <div className="mb-6" data-id="element-2836">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2837">
                              Minute
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-2838">
                              {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updatePartnerTimeOfBirth(partnerBaselineData.hour, m, partnerBaselineData.period)} className={`py-3 text-sm font-medium transition-colors ${partnerBaselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2839">
                                  {m}
                                </button>)}
                            </div>
                            <div className="mt-2" data-id="element-2840">
                              <input type="number" min="0" max="59" value={partnerBaselineData.minute} onChange={e => {
                          const val = e.target.value.padStart(2, '0');
                          if (parseInt(val) >= 0 && parseInt(val) <= 59) updatePartnerTimeOfBirth(partnerBaselineData.hour, val, partnerBaselineData.period);
                        }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2841" />
                            </div>
                          </div>
                          <div data-id="element-2842">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2843">
                              Period
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-2844">
                              {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updatePartnerTimeOfBirth(partnerBaselineData.hour, partnerBaselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${partnerBaselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2845">
                                  {p}
                                </button>)}
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>
                </div>
                <button onClick={handlePartnerBaselineSubmit} disabled={!partnerBaselineData.partnerName || !partnerBaselineData.dateOfBirth || !partnerBaselineData.placeOfBirth || !partnerBaselineData.country} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2846">
                  Generate Relationship Snapshot
                </button>
              </div>}

            {/* PHASE 2: Relationship Snapshot */}
            {currentPhase === 'relationship-snapshot' && <div data-id="element-2847">
                <div className="mb-12" data-id="element-2848">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2849">
                    Relationship Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2850">
                    Structural interaction
                  </p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2851">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2852">
                    <span className="text-2xl" data-id="element-2853">🧲</span>
                    <div className="flex-1" data-id="element-2854">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2855">
                        Why this bond formed
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2856">
                        {relationshipSnapshot.attractionMechanics}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2857">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2858">
                    <span className="text-2xl" data-id="element-2859">🧬</span>
                    <div className="flex-1" data-id="element-2860">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2861">
                        How the systems fit
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2862">
                        {relationshipSnapshot.patternFit}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2863">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2864">
                    <span className="text-2xl" data-id="element-2865">⚠️</span>
                    <div className="flex-1" data-id="element-2866">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2867">
                        Where tension is inevitable
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2868">
                        {relationshipSnapshot.frictionZones}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2869">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2870">
                    <span className="text-2xl" data-id="element-2871">⏳</span>
                    <div className="flex-1" data-id="element-2872">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2873">
                        What phase this relationship is in
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2874">
                        {relationshipSnapshot.currentBondPhase}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 text-center" data-id="element-2875">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-2876">
                    Structural relationship baseline — independent of effort or
                    intent
                  </p>
                </div>
                <div className="mb-12" data-id="element-2877">
                  <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-2878">
                    <span data-id="element-2879">📘</span>
                    <span data-id="element-2880">How this relationship snapshot is generated</span>
                    <span className="text-xs" data-id="element-2881">
                      {showExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2882">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-2883">
                        This snapshot is generated by analyzing two independent
                        structural baselines and how they interact.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed mb-4" data-id="element-2884">
                        It does not analyze behavior, communication style, or
                        effort.
                      </p>
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2885">
                        The next questions do not change the structure of the
                        bond. They localize where friction is currently being
                        experienced.
                      </p>
                    </div>}
                </div>
                <div className="space-y-4" data-id="element-2886">
                  <button onClick={() => setCurrentPhase('relationship-context')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2887">
                    🧩 Add relationship context (optional)
                  </button>
                  <div className="text-center" data-id="element-2888">
                    <p className="text-xs text-[#F5F5F5]/50 mb-2" data-id="element-2889">
                      Optional · 4 quick questions
                    </p>
                    <p className="text-xs text-[#F5F5F5]/40 leading-relaxed max-w-md mx-auto" data-id="element-2890">
                      This localizes where friction is showing up right now.
                    </p>
                  </div>
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-2891">
                    Save Report
                  </button>
                </div>
              </div>}

            {/* PHASE 3: Relationship Context */}
            {currentPhase === 'relationship-context' && <div data-id="element-2892">
                <div className="mb-12" data-id="element-2893">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2894">
                    Add context
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2895">
                    Optional · 4 quick questions
                  </p>
                </div>
                <div className="space-y-12 mb-12" data-id="element-2896">
                  <div data-id="element-2897">
                    <h2 className="text-lg font-medium mb-6" data-id="element-2898">
                      Relationship type
                    </h2>
                    <div className="space-y-3" data-id="element-2899">
                      {['Romantic', 'Marriage', 'Parent–child', 'Siblings', 'Professional partnership'].map(type => <label key={type} className={`block border p-5 cursor-pointer transition-colors ${relationshipContext.relationshipType === type ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2900">
                          <input type="radio" name="relationshipType" value={type} checked={relationshipContext.relationshipType === type} onChange={e => setRelationshipContext(prev => ({
                      ...prev,
                      relationshipType: e.target.value
                    }))} className="sr-only" data-id="element-2901" />
                          <span className="text-base" data-id="element-2902">{type}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-2903">
                    <h2 className="text-lg font-medium mb-6" data-id="element-2904">Duration</h2>
                    <div className="space-y-3" data-id="element-2905">
                      {['Less than 1 year', '1–3 years', '3–7 years', '7+ years'].map(duration => <label key={duration} className={`block border p-5 cursor-pointer transition-colors ${relationshipContext.durationBand === duration ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2906">
                          <input type="radio" name="durationBand" value={duration} checked={relationshipContext.durationBand === duration} onChange={e => setRelationshipContext(prev => ({
                      ...prev,
                      durationBand: e.target.value
                    }))} className="sr-only" data-id="element-2907" />
                          <span className="text-base" data-id="element-2908">{duration}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-2909">
                    <h2 className="text-lg font-medium mb-3" data-id="element-2910">
                      Where does tension show up most right now?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-2911">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-2912">
                      {['Communication', 'Control / freedom', 'Emotional safety', 'Distance / closeness', 'Responsibility imbalance'].map(zone => <label key={zone} className={`block border p-5 cursor-pointer transition-colors ${relationshipContext.tensionZones.includes(zone) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2913">
                          <input type="checkbox" value={zone} checked={relationshipContext.tensionZones.includes(zone)} onChange={() => handleToggleTensionZone(zone)} className="sr-only" data-id="element-2914" />
                          <span className="text-base" data-id="element-2915">{zone}</span>
                        </label>)}
                    </div>
                  </div>
                  <div data-id="element-2916">
                    <h2 className="text-lg font-medium mb-3" data-id="element-2917">
                      Anything important about this bond right now?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-2918">Optional</p>
                    <textarea value={relationshipContext.contextNote} onChange={e => setRelationshipContext(prev => ({
                  ...prev,
                  contextNote: e.target.value
                }))} rows={3} maxLength={200} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional..." data-id="element-2919" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-2920">
                      {relationshipContext.contextNote.length}/200
                    </p>
                  </div>
                </div>
                <button onClick={handleRelationshipContextSubmit} disabled={!relationshipContext.relationshipType || !relationshipContext.durationBand} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2921">
                  Apply Context
                </button>
              </div>}

            {/* PHASE 4: Full Relationship Diagnostic */}
            {currentPhase === 'full-relationship-diagnostic' && <div data-id="element-2922">
                <div className="mb-12" data-id="element-2923">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2924">
                    Relationship Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2925">Full Diagnostic</p>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2926">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2927">
                    <span className="text-2xl" data-id="element-2928">🧱</span>
                    <div className="flex-1" data-id="element-2929">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-2930">
                        How these systems interact by default
                      </p>
                      <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-2931">
                        {fullDiagnosticOutput.structuralInteraction}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2932">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2933">
                    <span className="text-2xl" data-id="element-2934">🔄</span>
                    <div className="flex-1" data-id="element-2935">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-2936">
                        How this interaction is expressing right now
                      </p>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2937">
                        {fullDiagnosticOutput.currentExpression}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2938">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2939">
                    <span className="text-2xl" data-id="element-2940">🪞</span>
                    <div className="flex-1" data-id="element-2941">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2942">
                        What each person triggers in the other
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2943">
                        {fullDiagnosticOutput.mutualTriggers}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2944">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2945">
                    <span className="text-2xl" data-id="element-2946">⚠️</span>
                    <div className="flex-1" data-id="element-2947">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2948">
                        What determines whether this bond matures or breaks
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2949">
                        {fullDiagnosticOutput.growthBreakpointBoundary}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mb-12" data-id="element-2950">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-2951">
                    <span data-id="element-2952">🔍</span>
                    <span data-id="element-2953">What changed after adding context</span>
                    <span className="text-xs" data-id="element-2954">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-2955">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2956">
                        <h3 className="text-sm font-medium mb-4" data-id="element-2957">
                          What baseline showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2958">
                          {fullDiagnosticOutput.whatChanged.baseline}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2959">
                        <h3 className="text-sm font-medium mb-4" data-id="element-2960">
                          What context revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2961">
                          {fullDiagnosticOutput.whatChanged.context}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2962">
                        <h3 className="text-sm font-medium mb-4" data-id="element-2963">
                          Why conflict ≠ incompatibility
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2964">
                          {fullDiagnosticOutput.whatChanged.whyConflictNotIncompatibility}
                        </p>
                      </div>
                    </div>}
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2965">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2966">
                    <span className="text-2xl" data-id="element-2967">🔁</span>
                    <div className="flex-1" data-id="element-2968">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2969">
                        Pattern Consistency Check
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed mb-6" data-id="element-2970">
                        This interaction pattern is structural, not situational.
                        The same dynamics appear across time and circumstances
                        because they emerge from how these two systems are
                        wired, not from what either person is doing wrong.
                      </p>
                      <div className="space-y-4" data-id="element-2971">
                        <div data-id="element-2972">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-2973">
                            Similar dynamics in other close bonds
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-2974">
                            If you examine other significant relationships, you
                            will likely find similar patterns: one system seeks
                            space to process, the other seeks connection to
                            regulate. One system withdraws under pressure, the
                            other pursues. The specific people change, but the
                            structural interaction repeats.
                          </p>
                        </div>
                        <div data-id="element-2975">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-2976">
                            Repetition across time
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-2977">
                            The same friction points recur across the lifespan
                            of this relationship. Early bonding may have masked
                            them, but as pressure increases or novelty fades,
                            the structural patterns become more visible. The
                            repetition is not failure — it is the pattern
                            expressing under different conditions.
                          </p>
                        </div>
                        <div data-id="element-2978">
                          <h3 className="text-sm font-medium mb-2 text-[#F5F5F5]/80" data-id="element-2979">
                            Same friction despite different circumstances
                          </h3>
                          <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-2980">
                            Changing external circumstances — moving, changing
                            jobs, having children — does not resolve the core
                            friction. The circumstances shift, but the way these
                            two systems interact under pressure remains
                            structurally consistent. This is not about effort or
                            intent — it is about pattern collision.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2981">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2982">
                    <span className="text-2xl" data-id="element-2983">⚖️</span>
                    <div className="flex-1" data-id="element-2984">
                      <h2 className="text-xl font-medium mb-6" data-id="element-2985">
                        Where This Diagnosis Applies — and Where It Doesn't
                      </h2>
                      <div className="space-y-6" data-id="element-2986">
                        <div data-id="element-2987">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-2988">
                            This diagnosis explains:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-2989">
                            <li data-id="element-2990">
                              • Why attraction and friction coexist in this bond
                            </li>
                            <li data-id="element-2991">
                              • Why effort alone doesn't resolve structural
                              tension
                            </li>
                            <li data-id="element-2992">
                              • Why the same themes recur across time and
                              circumstances
                            </li>
                            <li data-id="element-2993">
                              • How two systems interact under their structural
                              constraints
                            </li>
                          </ul>
                        </div>
                        <div data-id="element-2994">
                          <h3 className="text-sm font-medium mb-3 text-[#F5F5F5]/80" data-id="element-2995">
                            This diagnosis does NOT explain:
                          </h3>
                          <ul className="space-y-2 text-sm text-[#F5F5F5]/70" data-id="element-2996">
                            <li data-id="element-2997">
                              • Relationship outcomes or whether this bond will
                              last
                            </li>
                            <li data-id="element-2998">
                              • Who is right or wrong in specific conflicts
                            </li>
                            <li data-id="element-2999">
                              • What should be done next or how to fix the
                              dynamic
                            </li>
                            <li data-id="element-3000">
                              • Daily moods, temporary phases, or situational
                              reactions
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-3001">
                  <div className="flex items-start gap-4 mb-4" data-id="element-3002">
                    <span className="text-2xl" data-id="element-3003">🧭</span>
                    <div className="flex-1" data-id="element-3004">
                      <h2 className="text-xl font-medium mb-3" data-id="element-3005">
                        Integration Summary
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-3006">
                        These are two systems that attracted through
                        complementarity and now experience friction through the
                        same structural differences. One system stabilizes
                        through internal processing and space; the other through
                        connection and expression. Under low pressure, this
                        creates balance; under high pressure, it creates
                        distance and pursuit. The pattern repeats because each
                        system is trying to regulate in ways that structurally
                        collide, not because either person is failing. The bond
                        will mature if both systems can hold their own
                        regulation without requiring the other to change.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-3007">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3008">
                    💾 Save Relationship Decode
                  </button>
                  <button onClick={() => {
                setCurrentPhase('partner-baseline-collection');
                setPartnerBaselineData({
                  partnerName: '',
                  dateOfBirth: '',
                  timeOfBirth: '',
                  timeKnown: 'not-sure',
                  placeOfBirth: '',
                  country: '',
                  year: '',
                  month: '',
                  day: '',
                  hour: '12',
                  minute: '00',
                  period: 'AM'
                });
                setRelationshipContext({
                  relationshipType: '',
                  durationBand: '',
                  tensionZones: [],
                  contextNote: ''
                });
              }} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-3009">
                    ➕ Add another relationship
                  </button>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}